/**
 * Button - Universal Button Component
 *
 * Replaces ALL raw <button> usage in the app.
 * Supports multiple variants, sizes, states, and the special "glow" variant.
 *
 * Hierarchy: BaseComponent → Button → IconButton, SubmitButton, etc.
 *
 * @example
 * <Button variant="primary">Click me</Button>
 * <Button variant="glow" icon={<Sparkles />}>AI Feature</Button>
 * <Button size="sm" loading>Processing...</Button>
 */
import React, { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/shared/lib/utils/cn";
import type { BaseButtonProps } from "@/shared/ui/base";

/**
 * Button variants using CVA (Class Variance Authority)
 * Allows composition of variant + size combinations
 */
const buttonVariants = cva(
  // Base classes applied to ALL buttons - clean, minimal animations
  "inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]",
  {
    variants: {
      variant: {
        // Primary: Main call-to-action (red brand color)
        primary: "bg-red-600 hover:bg-red-700 text-white",
        // Secondary: Subtle background
        secondary: "bg-gray-100 hover:bg-gray-200 text-gray-900",
        // Outline: Border only
        outline: "border border-gray-300 hover:bg-gray-50 text-gray-700",
        // Ghost: Minimal, hover effect only
        ghost: "hover:bg-gray-100 text-gray-700",
        // Destructive: For dangerous actions
        destructive: "bg-red-100 hover:bg-red-200 text-red-700",
        // Link: Looks like a link
        link: "text-red-600 hover:text-red-700 underline-offset-4 hover:underline",
        // Glow: Special animated gradient border (replaces AIGlowButton)
        glow: "relative bg-white text-gray-700 hover:bg-gray-50",
      },
      size: {
        sm: "text-sm px-3 py-1.5",
        md: "text-base px-4 py-2",
        lg: "text-lg px-6 py-3",
        icon: "p-2", // Square icon button
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      fullWidth: false,
    },
  },
);

export interface ButtonProps
  extends BaseButtonProps, VariantProps<typeof buttonVariants> {
  /** Left icon element */
  icon?: React.ReactNode;
  /** Right icon element */
  iconRight?: React.ReactNode;
  /** Loading state - shows spinner */
  loading?: boolean;
  /** Button takes full width of container */
  fullWidth?: boolean;
}

/**
 * Button component
 *
 * Automatically handles loading states, icons, and variants.
 * The "glow" variant provides the animated gradient border effect.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      fullWidth = false,
      className,
      icon,
      iconRight,
      loading = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    // Glow variant requires special wrapper elements
    const isGlowVariant = variant === "glow";

    const buttonContent = (
      <>
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {!loading && icon && icon}
        {children}
        {!loading && iconRight && iconRight}
      </>
    );

    const buttonClasses = cn(
      buttonVariants({ variant, size, fullWidth }),
      className,
    );

    if (isGlowVariant) {
      return (
        <button
          ref={ref}
          disabled={disabled || loading}
          className={cn(buttonClasses, "btn-ai-glow")}
          {...props}
        >
          {buttonContent}
        </button>
      );
    }

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={buttonClasses}
        {...props}
      >
        {buttonContent}
      </button>
    );
  },
);

Button.displayName = "Button";

/**
 * IconButton - Convenience component for icon-only buttons
 */
export const IconButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, "icon" | "children"> & { icon: React.ReactNode }
>(({ icon, ...props }, ref) => (
  <Button ref={ref} size="icon" {...props}>
    {icon}
  </Button>
));

IconButton.displayName = "IconButton";
